// Owns: WebGPURenderer, scene, lights, camera rig, character views, arena, particles, post.
// Driven by main: feed it snapshots + alpha each frame.
// Construction is two-phase: `new Renderer(canvas)` sets up synchronous scene
// graph; `await renderer.init()` initializes the WebGPU backend and builds
// resources that require it (PMREM envmap, PostPipeline).

import * as THREE from 'three';
import { PMREMGenerator, WebGPURenderer } from 'three/webgpu';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { resolveUrl as resolveAssetUrl } from '../ui/editor/EditorAssets';
import { initKtx2Loader, whenKtx2Ready, whenKtx2Idle } from './textures/ktx2Loader';
import { CONFIG } from '../config';
import type { SimState } from '../sim/SimState';
import type { World as SimWorld } from '../sim/World';
import type { MapArena } from '../sim/MapArena';
import { MapArenaView } from './MapArenaView';
import { CameraRig } from './CameraRig';
import { CharacterView } from './CharacterView';
import { Particles } from './Particles';
import { PostPipeline } from './post/PostPipeline';
import { getBundle } from '../maps/registry';
import { setLiveRendererHook } from '../ui/graphicsSettings';

export class Renderer {
  readonly renderer: WebGPURenderer;
  readonly scene = new THREE.Scene();
  readonly cameraRig: CameraRig;
  readonly characterViews = new Map<number, CharacterView>();
  readonly arena: MapArenaView;
  readonly particles = new Particles();
  post!: PostPipeline;
  private dirLight: THREE.DirectionalLight;
  // Hemisphere fill light for the 'hemisphere' IBL mode. Always lives in
  // the scene; visibility toggled by setIbl() so swapping modes doesn't
  // touch the scene graph.
  private hemiLight: THREE.HemisphereLight;
  // Cached PMREMs. RoomEnvironment is built once at init(); the HDR is
  // built lazily on first switch to 'hdr' mode. We hold the render targets
  // so we can swap textures cheaply on mode change without rebuilding.
  private pmrem: PMREMGenerator | null = null;
  // PMREMGenerator under the WebGPU backend returns a base RenderTarget,
  // not a WebGLRenderTarget — type as RenderTarget so both backends work.
  private roomEnvRT: THREE.RenderTarget | null = null;
  private hdrEnvRT: THREE.RenderTarget | null = null;
  private hdrLoadedPath: string | null = null;
  private hdrLoading = false;
  // Promise for the most recently-issued HDR load. awaitAssetsReady() blocks
  // on this so the first frame doesn't render with the room-env fallback.
  private hdrLoadPromise: Promise<void> | null = null;
  private timeSec = 0;
  // Rapier collider wireframe overlay (toggleable)
  private wireframe: THREE.LineSegments;
  private simWorld: SimWorld | null = null;
  // `worldToScreen` allocates a fresh Vector3 otherwise — reuse this one to
  // avoid dropping GC garbage every frame (it's called ~4× per render).
  private _projOut = new THREE.Vector3();
  // Per-render scratch + caches. Reused every frame to avoid GC churn:
  //  - `_lightDir` holds the inverted, normalised dir light direction so
  //    `setLightDir` doesn't allocate-and-discard a Vector3 per character.
  //  - `_prevById` maps character id → snapshot so the prev lookup is O(1)
  //    instead of `prev.characters.find()` per character per frame.
  private _lightDir = new THREE.Vector3();
  private _prevById = new Map<number, import('../sim/SimState').CharacterSnapshot>();

  constructor(canvas: HTMLCanvasElement, mapId: string, simArena: MapArena) {
    const bundle = getBundle(mapId);
    if (!bundle) {
      throw new Error(
        `[Renderer] map bundle not found for mapId "${mapId}". ` +
        `Ensure src/maps/bundles/<bundle>/map.json declares this id.`
      );
    }
    this.arena = new MapArenaView(simArena, bundle);
    this.renderer = new WebGPURenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(this.computePixelRatio());
    this.renderer.setSize(innerWidth, innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x0b0d12, 1);
    // Shadows are gated by CONFIG.render.shadowsEnabled — a startup-time switch.
    // When off we skip three things together: (1) the renderer's shadow pass,
    // (2) `castShadow` on the dir light, (3) wiring the dir light into custom
    // NodeMaterials. The third matters because materials like ToonMaterial /
    // LavaMaterial bake a `shadow(light)` node into their colorNode when given
    // a light — once baked, disabling the shadow pass leaves a dangling node
    // with no shadow map bound and the WebGPU shader build trips. Skipping
    // the wiring lets those materials take their `float(1)` fallback branch.
    if (CONFIG.render.shadowsEnabled) {
      // Soft cast shadows. PCFSoftShadowMap dilates the PCF kernel for a
      // pleasant fuzzy edge without the light-bleed artefacts of VSM. The
      // type must be set BEFORE any material first samples a shadow node,
      // since the WebGPU shadow node bakes its filter at shader-build time.
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    this.cameraRig = new CameraRig(innerWidth / innerHeight);
    // Hand the listener to the arena view so render-layer behaviors can
    // spawn their own THREE.PositionalAudio sources without us threading
    // it through every constructor.
    this.arena.setAudioListener(this.cameraRig.listener);

    // Lights — IBL supplies fill, so these are the "key + rim" layer.
    const amb = new THREE.AmbientLight(0xffffff, 0.75);
    this.scene.add(amb);
    this.dirLight = new THREE.DirectionalLight(0xfff5d8, 4.8);
    this.dirLight.position.set(-3.5, 6, 3);
    // Per-map sun direction — the spaceship map exposes a world-direction
    // toward its sun so the directional light (and the toon shading that
    // reads it) agrees with the sky/ring/rock shaders. Other maps leave
    // the default.
    const mapSun = this.arena.sunDir;
    if (mapSun) this.dirLight.position.copy(mapSun).multiplyScalar(10);
    // Shadow camera covers the play area; tighten if a map ever exceeds it.
    this.dirLight.castShadow = CONFIG.render.shadowsEnabled;
    if (CONFIG.render.shadowsEnabled) {
      const sz = Math.max(1, CONFIG.render.shadowMapSize);
      this.dirLight.shadow.mapSize.set(sz, sz);
      const sc = this.dirLight.shadow.camera;
      sc.left = -30; sc.right = 30; sc.top = 30; sc.bottom = -30;
      sc.near = 0.5; sc.far = 80;
      sc.updateProjectionMatrix();
      this.dirLight.shadow.bias = -0.0005;
      this.dirLight.shadow.normalBias = 0.04;
      this.dirLight.shadow.radius = 4;
    }
    this.scene.add(this.dirLight);

    // Hemisphere fill — only enabled in iblMode==='hemisphere'. Sky/ground
    // colors and intensity are driven from CONFIG.render via setIbl(). Kept
    // in the scene at all times so toggling modes is just a visibility flip.
    this.hemiLight = new THREE.HemisphereLight(
      new THREE.Color(CONFIG.render.iblHemiSky),
      new THREE.Color(CONFIG.render.iblHemiGround),
      CONFIG.render.iblHemiIntensity
    );
    this.hemiLight.visible = false;
    this.scene.add(this.hemiLight);

    this.scene.add(this.arena.group);
    this.scene.add(this.particles.mesh);

    // Wireframe overlay (Rapier debug-render output)
    const wfGeo = new THREE.BufferGeometry();
    wfGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    wfGeo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(0), 4));
    const wfMat = new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false, transparent: true });
    this.wireframe = new THREE.LineSegments(wfGeo, wfMat);
    this.wireframe.frustumCulled = false;
    this.wireframe.visible = false;
    this.wireframe.renderOrder = 999;
    this.scene.add(this.wireframe);

    addEventListener('resize', this.handleResize);
  }

  async init(): Promise<void> {
    await this.renderer.init();

    // Wire up the shared KTX2 loader before any material constructor runs —
    // every map texture and env map ships as Basis-compressed .ktx2 now, so
    // the loader needs detectSupport() against this WebGPU renderer to pick
    // the right transcoded GPU format.
    initKtx2Loader(this.renderer);

    // Scene environment (IBL) — character body uses a custom ToonMaterial that
    // ignores this, but anything else (arena stairs, imported GLBs like hats)
    // is MeshStandardMaterial, which needs an envMap to read properly. Without
    // IBL, non-metal surfaces get fill *only* from direct lights, so darker
    // base colors render near-black even with strong ambient. RoomEnvironment
    // is three's built-in neutral studio procedural — it provides the kind of
    // baseline environment contribution Blender gives you for free.
    // PMREM generator is reused for any HDR loaded later, so keep it alive
    // for the renderer's lifetime instead of disposing after the room env.
    this.pmrem = new PMREMGenerator(this.renderer);
    this.roomEnvRT = this.pmrem.fromScene(new RoomEnvironment(), 0.04);
    // Per-map IBL override — copied onto CONFIG so the debug panel still
    // edits the live values after init. Each field is independently optional.
    const ibl = this.arena.ibl;
    if (ibl) {
      const r = CONFIG.render;
      if (ibl.mode          !== undefined) r.iblMode          = ibl.mode;
      if (ibl.intensity     !== undefined) r.iblIntensity     = ibl.intensity;
      if (ibl.rotationDeg   !== undefined) r.iblRotationDeg   = ibl.rotationDeg;
      if (ibl.hemiSky       !== undefined) r.iblHemiSky       = ibl.hemiSky;
      if (ibl.hemiGround    !== undefined) r.iblHemiGround    = ibl.hemiGround;
      if (ibl.hemiIntensity !== undefined) r.iblHemiIntensity = ibl.hemiIntensity;
      if (ibl.hdrPath       !== undefined) r.iblHdrPath       = ibl.hdrPath;
    }
    // Apply the configured starting IBL mode + intensity + rotation.
    this.applyIbl();

    // Render-layer behavior init is async — GLB loads, texture fetches.
    await this.arena.initRenderBehaviors();

    // Drain the initial wave of KTX2 transcodes (and any HDR IBL load kicked
    // off by applyIbl above) so first-frame materials have real texture data
    // instead of 1×1 placeholders. Without this, low-end devices render a
    // few frames as solid white before the textures pop in.
    await this.awaitAssetsReady();

    // Wire the key light's cast-shadow into any custom NodeMaterial that
    // opts in (RockMaterial, RopeMaterial, …). Standard materials get
    // shadow reception for free via the renderer's lighting path; this is
    // only for the materials that override colorNode and need to sample
    // shadow() explicitly. Character ToonMaterials are handled at
    // addCharacter time, since they're created later.
    // Skipped entirely when shadows are off — see the constructor comment.
    //
    // The traverse used to walk `this.scene` — including the lights,
    // hemisphere fill, particles InstancedMesh, wireframe LineSegments,
    // and every visit's `Array.isArray(mesh.material) ? ... : [...]`
    // wrapper. None of those carry wirable NodeMaterials. Scoping to
    // `this.arena.group` skips the Scene-level non-mesh siblings; the
    // arena subtree is the only place wirable map materials live. Init
    // hitch only — but on heavier maps that's a few hundred meshes.
    if (CONFIG.render.shadowsEnabled) {
      this.arena.group.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!(mesh as { isMesh?: boolean }).isMesh) return;
        const m = mesh.material;
        if (Array.isArray(m)) {
          for (let i = 0; i < m.length; i++) {
            (m[i] as { setDirectionalLight?: (l: THREE.DirectionalLight) => void })
              .setDirectionalLight?.(this.dirLight);
          }
        } else {
          (m as { setDirectionalLight?: (l: THREE.DirectionalLight) => void })
            .setDirectionalLight?.(this.dirLight);
        }
      });
    }

    this.post = new PostPipeline(this.renderer, this.scene, this.cameraRig.camera, innerWidth, innerHeight);

    // Register as the live match renderer so the settings layer can push
    // graphics changes mid-match. Cleared by Renderer.disposeLifecycle()
    // on teardown.
    Renderer.active = this;
    setLiveRendererHook(this);
  }

  /** Push the current CONFIG.render values into the live renderer. Called
   *  by graphicsSettings.applyGraphicsSettingsToConfig whenever a setting
   *  is toggled mid-match.
   *
   *  Live-safe knobs: renderScale (pixel ratio + size), all post-pass
   *  settings the PostPipeline reads each frame (bloom, AA, motion blur,
   *  SSAO, anamorphic, LUT), and IBL via applyIbl().
   *
   *  Limitation: shadows on/off is NOT live. Materials bake a shadow
   *  node into their colorNode at shader-build time when shadows are
   *  enabled, and toggling them mid-match would leave a dangling node
   *  with no shadow map bound. The shadowMapSize change does take live
   *  effect (the shadow map texture re-allocates on next render). */
  applyGraphicsLive(): void {
    // renderScale → push the new pixel ratio + nudge size so the
    // backbuffer reallocates at the new resolution.
    this.renderer.setPixelRatio(this.computePixelRatio());
    this.renderer.setSize(innerWidth, innerHeight, false);
    this.post?.setSize(innerWidth, innerHeight);

    // shadowMapSize live-update — the directional light's shadow camera
    // re-allocates its render target on the next render frame.
    if (CONFIG.render.shadowsEnabled && this.dirLight.shadow) {
      const sz = Math.max(1, CONFIG.render.shadowMapSize);
      this.dirLight.shadow.mapSize.set(sz, sz);
      // Force the shadow map to regenerate at the new resolution.
      this.dirLight.shadow.map?.dispose();
      this.dirLight.shadow.map = null as unknown as THREE.WebGLRenderTarget;
    }

    // Cinematic Lighting (HDR vs hemisphere vs off) — applyIbl handles
    // all three branches and tears down/rebuilds the env map cleanly.
    this.applyIbl();
  }

  /** Match-teardown hook. Drop from the live-renderer slot so subsequent
   *  settings changes don't fan out to a now-disposed renderer. Idempotent.
   *  Call this from main.ts before disposing the WebGPURenderer backend. */
  disposeLifecycle(): void {
    if (Renderer.active === this) {
      Renderer.active = null;
      setLiveRendererHook(null);
    }
  }

  /** The currently-active match renderer, or null when no match is live.
   *  graphicsSettings reads this to push live updates. Single-renderer
   *  assumption matches the codebase: only one match runs at a time. */
  static active: Renderer | null = null;

  /** Reapply the IBL configuration from CONFIG.render (mode, intensity,
   *  rotation, hemi colors, hdr path). Called once at init() and from the
   *  debug panel whenever any iblXxx field changes. Cheap when the cubemap
   *  is already cached; the 'hdr' branch kicks off an async load on first
   *  switch and re-applies once the texture is ready. */
  applyIbl(): void {
    const c = CONFIG.render;
    // Hemisphere is independent of the env cubemap path — drive it always
    // and toggle visibility based on mode.
    this.hemiLight.color.set(c.iblHemiSky);
    this.hemiLight.groundColor.set(c.iblHemiGround);
    this.hemiLight.intensity = c.iblHemiIntensity;
    this.hemiLight.visible = c.iblMode === 'hemisphere';

    // Apply intensity + rotation (the rotation is harmless on a null env).
    this.scene.environmentIntensity = c.iblIntensity;
    const yaw = c.iblRotationDeg * Math.PI / 180;
    this.scene.environmentRotation = new THREE.Euler(0, yaw, 0);

    switch (c.iblMode) {
      case 'off':
        // No IBL at all. Standard materials lose env-derived diffuse/specular
        // and render from direct lights only — used by the user's
        // Cinematic Lighting=off setting to fully skip IBL sampling.
        this.scene.environment = null;
        break;
      case 'roomDefault':
      case 'roomAligned':
        this.scene.environment = this.roomEnvRT?.texture ?? null;
        break;
      case 'hemisphere':
        // No IBL specular in hemi mode — pure sky/ground gradient diffuse.
        this.scene.environment = null;
        break;
      case 'hdr': {
        // Use the cached HDR if its path matches; otherwise (re)load. The
        // mode is also re-applied when the load resolves, so the user sees
        // whatever the previous env was until the HDR is ready.
        if (this.hdrEnvRT && this.hdrLoadedPath === c.iblHdrPath) {
          this.scene.environment = this.hdrEnvRT.texture;
        } else {
          this.loadHdrEnv(c.iblHdrPath);
          // Fall back to room env so the scene isn't pitch-black during load.
          this.scene.environment = this.roomEnvRT?.texture ?? null;
        }
        break;
      }
    }
  }

  /** Invalidate the cached HDR PMREM and re-apply IBL so the file is
   *  refetched from disk. Useful after replacing the HDR asset. */
  reloadHdr(): void {
    this.hdrEnvRT?.dispose();
    this.hdrEnvRT = null;
    this.hdrLoadedPath = null;
    this.applyIbl();
  }

  /** Compute a yaw (degrees) that brings RoomEnvironment's brightest panels
   *  roughly into agreement with the map's `sunDir`. The user can fine-tune
   *  from there via the rotation slider. */
  getSunAlignedRotationDeg(): number {
    const s = this.arena.sunDir;
    if (!s) return 0;
    return Math.atan2(s.x, s.z) * 180 / Math.PI;
  }

  private loadHdrEnv(path: string): void {
    if (this.hdrLoading || !this.pmrem) return;
    this.hdrLoading = true;
    // Env maps are still equirectangular — just stored as Basis-compressed
    // KTX2 instead of RGBE .hdr after the asset compaction pass. The Basis
    // codec is LDR (ETC1S/UASTC), so the original HDR range was clamped at
    // compaction time; PMREM still produces a usable IBL from the LDR pano.
    this.hdrLoadPromise = whenKtx2Ready().then((loader) => new Promise<void>((resolve) => {
      loader.load(
        resolveAssetUrl(path),
        (tex) => {
          tex.mapping = THREE.EquirectangularReflectionMapping;
          const rt = this.pmrem!.fromEquirectangular(tex);
          tex.dispose();
          this.hdrEnvRT?.dispose();
          this.hdrEnvRT = rt;
          this.hdrLoadedPath = path;
          this.hdrLoading = false;
          // Re-apply so the new texture takes effect if we're still in 'hdr'
          // mode (the user may have switched away while we were loading).
          if (CONFIG.render.iblMode === 'hdr') this.applyIbl();
          resolve();
        },
        undefined,
        (err) => {
          console.warn(`[Renderer] failed to load env KTX2 "${path}":`, err);
          this.hdrLoading = false;
          resolve();
        }
      );
    }));
  }

  /** Resolves once every async asset load triggered so far has settled —
   *  KTX2 texture transcodes plus any pending HDR IBL load. Loops until idle
   *  so HDR completion (which can re-call applyIbl) doesn't sneak past.
   *  Call this after any path that may issue new IBL loads (e.g.
   *  applyUserIblAfterInit) before kicking off the render loop. */
  async awaitAssetsReady(): Promise<void> {
    for (;;) {
      const hdr = this.hdrLoadPromise;
      await whenKtx2Idle();
      if (hdr) await hdr;
      // If applyIbl was called during the await and queued a new HDR load,
      // loop once more. Otherwise we're idle.
      if (!this.hdrLoading && this.hdrLoadPromise === hdr) return;
    }
  }

  addCharacter(id: number, view: CharacterView): void {
    this.characterViews.set(id, view);
    // Wire the scene's directional light into the toon material so its
    // colorNode picks up cast-shadow attenuation. This rebuilds the node
    // graph once per material; subsequent renders use the cached shader.
    // Skipped when shadows are off — the toon material falls back to its
    // `float(1)` no-shadow branch and never builds the shadow() node.
    if (CONFIG.render.shadowsEnabled) {
      view.material.setDirectionalLight(this.dirLight);
    }
    this.scene.add(view.group);
  }

  /** Inject the sim world so the renderer can call `world.debugRender()` for wireframes. */
  setSimWorld(w: SimWorld): void {
    this.simWorld = w;
    this.cameraRig.setOcclusionWorld(w?.rapier ?? null);
  }

  /** Per-rAF render. timeScale lets HitStop/SlowMo tug visual time. cameraYaw
   * is supplied externally so the camera orbits with the mouse, not with the
   * character's body yaw (which now turns to face the WASD wish direction).
   * `focusId` selects which character the camera follows (spectate mode
   * switches this away from id 0 once the local player dies).
   *
   * `liveRemoteBones` is the live per-rAF bone accessor (ColyseusTransport's
   * `remoteBonesAt`). When supplied and it returns non-null bones for a
   * character, those bones drive the meshes directly — bypassing the prev/curr
   * alpha-lerp, which can't produce sub-step motion for remotes whose synth
   * inputs come from the same 60Hz bracket curve. The local character's
   * accessor returns null, so it always falls through to the alpha-lerp path
   * (fed by Rapier prev/curr — already smooth at the rAF rate).
   * Pass undefined while KO replay is active so historical bones from
   * `snapshotAtTick` drive the scene through the existing alpha-lerp. */
  render(prev: SimState | null, curr: SimState | null, alpha: number, realDt: number, timeScale: number, cameraYaw: number, cameraPitch: number, focusId = 0, liveRemoteBones?: (charId: number) => Float32Array | null): void {
    const dt = realDt * timeScale;
    this.timeSec += dt;

    // Update character meshes by id-matched snapshots. Build a small id-keyed
    // Map of `prev` once so the inner loop's prev lookup is O(1) instead of
    // O(n) `Array.find()` per character per frame.
    const prevById = this._prevById;
    prevById.clear();
    if (prev) for (const p of prev.characters) prevById.set(p.id, p);
    if (curr) {
      for (const cs of curr.characters) {
        const view = this.characterViews.get(cs.id);
        if (!view) continue;
        const liveBones = liveRemoteBones?.(cs.id) ?? null;
        if (liveBones) {
          view.updateLiveBones(liveBones, dt);
        } else {
          const ps = prevById.get(cs.id) ?? null;
          view.update(ps, cs, alpha, dt);
        }
      }
    }

    // Camera follows the focused character's pelvis. `focusId` is normally
    // the local player but flips to a teammate when the human is spectating.
    // Yaw comes from the mouse-driven cameraYaw, NOT from the character's
    // body — so moving the character does not swing the camera around them.
    //
    // Camera lerp uses `realDt` (not the timeScale-scaled `dt`). HitStop
    // sets timeScale to 0 on impact, which would zero the camera's lerp
    // coefficient and freeze it while the sim kept advancing the focus
    // character's pelvis in the background — producing a visible jolt as
    // the camera caught up when hitstop released. Decoupling the camera
    // from timeScale keeps the follow smooth through every impact.
    if (curr && curr.characters.length > 0) {
      let focus = curr.characters[0]!;
      for (let i = 0; i < curr.characters.length; i++) {
        const c = curr.characters[i]!;
        if (c.id === focusId) { focus = c; break; }
      }
      // Head pose is at bone index 2 in `bones`: [px, py, pz, qx, qy, qz, qw].
      // While swimming we track the head directly (and lift the safety floor)
      // so the cam dunks under with the bobbing body. Otherwise we follow
      // pelvis + 0.6 m as before — that height was authored as the look
      // target for stand / sprint / KO and shouldn't change for those.
      const HEAD_BONE = 2;
      const off = HEAD_BONE * 7;
      const fx = focus.isSwimming ? focus.bones[off + 0]! : focus.pelvisX;
      const fy = focus.isSwimming ? focus.bones[off + 1]! : focus.pelvisY + 0.6;
      const fz = focus.isSwimming ? focus.bones[off + 2]! : focus.pelvisZ;
      this.cameraRig.update(fx, fy, fz, cameraYaw, cameraPitch, realDt, focus.isSwimming);
      // Pin the DOF focus plane to the same point the camera is tracking —
      // keeps the focused character sharp through camera swings / spectate.
      this.post?.setFocusTarget(fx, fy, fz);
    }

    this.particles.update(dt, this.cameraRig.camera);

    // Behaviors own all per-map render sync (material setTime pump, emitter
    // update, ship-group → kinematic body sync, rope mesh → rope body sync,
    // ion thrusters). Renderer stays generic.
    this.arena.setActiveCamera(this.cameraRig.camera);
    this.arena.stepRender(this.timeSec, dt, alpha);

    // Toon material light dir comes from the dir light position. Compute it
    // ONCE per frame into a scratch vector instead of cloning per character —
    // the dir light position is shared across every toon material, and the
    // previous code allocated and discarded a Vector3 per character per frame.
    const ld = this._lightDir.copy(this.dirLight.position).normalize().multiplyScalar(-1);
    for (const view of this.characterViews.values()) view.material.setLightDir(ld);

    // Update Rapier wireframe overlay if enabled.
    this.wireframe.visible = CONFIG.sim.showWireframes;
    if (CONFIG.sim.showWireframes && this.simWorld) {
      const dbg = this.simWorld.rapier.debugRender();
      const posAttr = this.wireframe.geometry.getAttribute('position') as THREE.BufferAttribute;
      const colAttr = this.wireframe.geometry.getAttribute('color') as THREE.BufferAttribute;
      if (posAttr.array.length !== dbg.vertices.length) {
        this.wireframe.geometry.setAttribute('position', new THREE.BufferAttribute(dbg.vertices, 3));
        this.wireframe.geometry.setAttribute('color', new THREE.BufferAttribute(dbg.colors, 4));
      } else {
        (posAttr.array as Float32Array).set(dbg.vertices);
        (colAttr.array as Float32Array).set(dbg.colors);
        posAttr.needsUpdate = true;
        colAttr.needsUpdate = true;
      }
    }

    this.post.render(dt);
  }

  setArenaDissolve(v: number): void { this.arena.setDissolve(v); }

  /** Project a world-space point to CSS pixel coordinates on the canvas.
   *  Used by the world-space name-tag overlay. `onScreen` is false if the
   *  point is behind the camera (negative w after projection) — callers
   *  should hide labels in that case. `depth01` is roughly 0 at the near
   *  plane, 1 at the far plane — tags can scale with this to match
   *  perspective. */
  worldToScreen(x: number, y: number, z: number): {
    x: number; y: number; onScreen: boolean; depth01: number;
  } {
    const v = this._projOut.set(x, y, z);
    const cam = this.cameraRig.camera;
    // project() returns NDC in [-1, 1] on each axis. Anything with ndc.z > 1
    // is behind the camera or past the far plane.
    v.project(cam);
    const onScreen = v.z > -1 && v.z < 1;
    const w = innerWidth, h = innerHeight;
    return {
      x: (v.x + 1) * 0.5 * w,
      y: (1 - v.y) * 0.5 * h,
      onScreen,
      depth01: (v.z + 1) * 0.5
    };
  }

  private handleResize = (): void => {
    const w = innerWidth, h = innerHeight;
    this.renderer.setPixelRatio(this.computePixelRatio());
    this.renderer.setSize(w, h, false);
    this.cameraRig.setAspect(w / h);
    this.post.setSize(w, h);
  };

  /** Pixel ratio fed to the WebGPU backend. Uses raw `devicePixelRatio`
   *  (no cap) multiplied by the user's Resolution setting. The previous
   *  `min(devicePixelRatio, 2)` cap broke zoom invariance: in Chrome,
   *  browser zoom scales DPR up while shrinking `innerWidth` inversely, so
   *  `innerWidth × DPR` is constant — but capping DPR turns zoom-in into a
   *  real resolution drop. Removing the cap means GPU resolution stays
   *  invariant under browser zoom, and the user's Resolution setting is
   *  the single lever for downscaling on high-DPR displays. */
  private computePixelRatio(): number {
    return devicePixelRatio * CONFIG.render.renderScale;
  }
}
